/** @jsx jsx */
import { jsx } from "frontend/src/utils/jsx"
import { SectionGrid, Grid } from "frontend/src/components/Layout"
import { useState, Fragment } from "react"
import { BlockText } from "frontend/src/components/BlockText"

// styles
const titleStyle = { fontFamily: "body", fontWeight: 500 }

// nutrition functions
function NutritionTable({ title, product }) {
  const { naehrwertePerHundred, portionSize, naehrwertePerPortion } = product
  const {
    energyKcal: KcalHundred,
    energyKj: KjHundred,
    fat: fatHundred,
    saturatedFat: saturatedHundred,
    carbs: carbsHundred,
    sugar: sugarHundred,
    protein: proteinHundred,
    fiber: fiberHundred,
    salt: saltHundred,
  } = naehrwertePerHundred
  const {
    energyKcal: KcalPortion,
    energyKj: KjPortion,
    fat: fatPortion,
    saturatedFat: saturatedPortion,
    carbs: carbsPortion,
    sugar: sugarPortion,
    protein: proteinPortion,
    fiber: fiberPortion,
    salt: saltPortion,
  } = naehrwertePerPortion
  return (
    <Grid
      columns="4fr 2fr 2fr"
      sx={{
        gridColumn: ["1", "1", "2 / 5", "2 / 5"],
        width: "100%",
        marginBottom: "25px",
        fontFamily: "body",
      }}
    >
      {/* Table heading */}
      <div
        sx={{
          variant: "text.body1",
          textTransform: "none",
          color: "almostBlack",
        }}
      >
        {title}
      </div>
      <div
        sx={{
          variant: "text.body1",
          textTransform: "none",
          color: "almostBlack",
          textAlign: "center",
        }}
      >
        100 ml
      </div>
      <div
        sx={{
          variant: "text.body1",
          textTransform: "none",
          color: "almostBlack",
          textAlign: "center",
        }}
      >
        {portionSize} ml Portion
      </div>
      <Line max={4} margin="10px" />
      {/* first row: Energy I */}
      <NutritionTableRow
        description="Energie"
        hundred={KcalHundred}
        portion={KcalPortion}
        unit="kcal"
      />
      {/* second row: Energy II */}
      <NutritionTableRow description="" hundred={KjHundred} portion={KjPortion} unit="kJ" />
      <Line max={4} margin="10px" />
      {/* third row: Fat I */}
      <NutritionTableRow description="Fett" hundred={fatHundred} portion={fatPortion} unit="g" />
      {/* fourth row: Fat II */}
      <NutritionTableRow
        description="(davon gesättigte Fettsäuren)"
        hundred={saturatedHundred}
        portion={saturatedPortion}
        unit="g"
      />
      <Line max={4} margin="10px" />
      {/* fifth row: Carbohydrates I */}
      <NutritionTableRow
        description="Kohlenhydrate"
        hundred={carbsHundred}
        portion={carbsPortion}
        unit="g"
      />
      {/* sixth row: Carbohydrates II */}
      <NutritionTableRow
        description="(davon Zucker)"
        hundred={sugarHundred}
        portion={sugarPortion}
        unit="g"
      />
      <Line max={4} margin="10px" />
      {/* seventh row: Fibers */}
      <NutritionTableRow
        description="Ballaststoffe"
        hundred={fiberHundred}
        portion={fiberPortion}
        unit="g"
      />
      <Line max={4} margin="10px" />
      {/* eighth row: Proteins */}
      <NutritionTableRow
        description="Protein"
        hundred={proteinHundred}
        portion={proteinPortion}
        unit="g"
      />
      <Line max={4} margin="10px" />
      {/* ninth row: Salt */}
      <NutritionTableRow description="Salz" hundred={saltHundred} portion={saltPortion} unit="g" />
    </Grid>
  )
}

function NutritionTableRow({ description, hundred, portion, unit }) {
  return (
    <Fragment>
      <div
        sx={{
          variant: "text.body2",
          color: "almostBlack",
          pl: "5px",
        }}
      >
        {description}
      </div>
      <div
        sx={{
          variant: "text.body2",
          color: "almostBlack",
          textAlign: "center",
        }}
      >
        {hundred} {unit}
      </div>
      <div
        sx={{
          variant: "text.body2",
          color: "almostBlack",
          textAlign: "right",
          pr: "10px",
        }}
      >
        {portion} {unit}
      </div>
    </Fragment>
  )
}

function NutritionActive({ title, active, message }) {
  return (
    <Grid
      columns="4fr 2fr"
      sx={{
        gridColumn: ["1", "1", "2 / 5", "2 / 5"],
        width: "100%",
        fontFamily: "body",
      }}
    >
      {/* Table heading */}
      <div
        sx={{
          variant: "text.body1",
          textTransform: "none",
          color: "almostBlack",
          gridColumn: "1 / 3",
          fontFamily: "heading",
        }}
      >
        {title}
      </div>
      <Line max={3} margin="10px" />
      {active.map((ingredient, index) => {
        const { amountPerServing: amount, euReference } = ingredient
        return (
          <Fragment key={index}>
            <div
              sx={{
                variant: "text.body2",
                color: "almostBlack",
                pl: "5px",
              }}
            >
              {ingredient.name}
            </div>
            <div
              sx={{
                variant: "text.body2",
                color: "almostBlack",
                textAlign: "right",
                pr: "5px",
              }}
            >
              {amount}
              {!euReference && <span>*</span>}
            </div>
            <Line max={3} margin="10px" />
          </Fragment>
        )
      })}
      <div
        sx={{
          variant: "text.body2",
          color: "almostBlack",
          gridColumn: "1 / 3",
          pl: "5px",
        }}
      >
        {message}
      </div>
    </Grid>
  )
}

function Line({ max, margin }) {
  if (margin) {
    return (
      <div
        sx={{
          height: ["1px", "0.38px", "0.6px", "1px"],
          width: "100%",
          borderColor: "almostBlack",
          borderStyle: "solid",
          borderWidth: ["1px", "0.384px", "0.6px", "1px"],
          gridColumn: `1 / ${max}`,
          marginY: `${margin}`,
        }}
      />
    )
  }
  return (
    <div
      sx={{
        height: ["1px", "0.38px", "0.6px", "1px"],
        width: "100%",
        borderColor: "almostBlack",
        borderStyle: "solid",
        borderWidth: ["1px", "0.384px", "0.6px", "1px"],
        gridColumn: `1 / ${max}`,
      }}
    />
  )
}

function Nutrition({ product }) {
  const { describedProduct, productDescription } = product
  const { ingredientList, activeIngredientsList } = describedProduct
  const titleActiveIngredients = "Inhaltsstoffe pro Portionsfläschchen"
  const messageReferenceAmount = "*Keine Referenzmenge durch die EU definiert"
  const titleNutritionTable = "Nährwerte je"
  return (
    <Grid columns={["1fr", "1fr", "1fr 1fr"]} gap="10px">
      <div
        sx={{
          gridColumn: "1 / span 1",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <div>
          <span sx={{ textTransform: "uppercase" }}>Zutatenliste: </span>
          {ingredientList[0].children[0].text}
        </div>
        <div>
          <NutritionActive
            title={titleActiveIngredients}
            active={activeIngredientsList}
            message={messageReferenceAmount}
          />
        </div>
      </div>
      <div
        sx={{
          gridColumn: ["1 /span 1", "1 / span 1", "2 / span 1"],
          display: "flex",
          flexDirection: "column",
          justifyContent: "spaceBetween",
        }}
      >
        <div sx={{ mb: "20px" }}>
          <p>{productDescription}</p>
        </div>
        <NutritionTable title={titleNutritionTable} product={describedProduct} />
      </div>
    </Grid>
  )
}

function AccordionSection({ section, isOpen, index, setOpen, last }) {
  const { title, productInfo, text } = section
  const borders = last
    ? {
        borderTopWidth: "1px",
        borderTopStyle: "solid",
        borderTopColor: "almostBlack",
        borderBottomWidth: "1px",
        borderBottomStyle: "solid",
        borderBottomColor: "almostBlack",
      }
    : { borderTopWidth: "1px", borderTopStyle: "solid", borderTopColor: "almostBlack" }
  if (isOpen) {
    return (
      <div
        sx={{
          py: 4,
          ...borders,
        }}
        onClick={() => setOpen(-1)}
      >
        <div sx={{ display: "flex", flexDirection: "row", justifyContent: "space-between", my: 4 }}>
          <h3 sx={{ ...titleStyle }}>{title}</h3>
          <div>&mdash;</div>
        </div>
        {productInfo && <Nutrition product={productInfo} />}
        {text && (
          <BlockText blocks={text} lang="de" style={{ hyphens: "auto", "-moz-hyphens": "auto" }} />
        )}
      </div>
    )
  }
  return (
    <div
      sx={{
        py: 4,
        ...borders,
      }}
      onClick={() => setOpen(index)}
    >
      <div sx={{ display: "flex", flexDirection: "row", justifyContent: "space-between" }}>
        <h3 sx={{ ...titleStyle }}>{title}</h3>
        <div>+</div>
      </div>
    </div>
  )
}

function AccordionProductInfo({ accordionSections }) {
  const [open, setOpen] = useState(-1)
  const lastItem = accordionSections.length - 1
  return (
    <SectionGrid sx={{ backgroundColor: "white", my: 5 }}>
      <SectionGrid.narrow sx={{ mx: [5, 5, 0], py: [7, 5] }}>
        {accordionSections.map((section, index) => (
          <AccordionSection
            key={index}
            section={section}
            isOpen={open === index}
            index={index}
            setOpen={setOpen}
            last={index === lastItem}
          />
        ))}
      </SectionGrid.narrow>
    </SectionGrid>
  )
}

export { AccordionProductInfo }
